import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Layers, FileText, Lock } from 'lucide-react';
import { getBandLabel } from './cfqScoring';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Module catalogue — maps directly to CFQ domains ────────────────────────
const MODULES = [
  { domain: 0, num: 1, title: 'Self-Anchoring',         subtitle: 'Identity & Integrity',        desc: 'Establish your cultural identity as a stable leadership foundation.',          duration: '45 min', free: true,  path: '/module-1' },
  { domain: 1, num: 2, title: 'Perspective-Shifting',   subtitle: 'Empathy & Understanding',     desc: 'Develop the ability to see situations through multiple cultural lenses.',      duration: '60 min', free: false, path: '/courses' },
  { domain: 2, num: 3, title: 'Adaptive Communication', subtitle: 'Expression & Context',        desc: 'Express clearly across cultures without losing your voice.',                  duration: '55 min', free: false, path: '/courses' },
  { domain: 3, num: 4, title: 'Relational Integration', subtitle: 'Collaboration & Influence',   desc: 'Build trust, influence, and cohesion across cultural boundaries.',            duration: '50 min', free: false, path: '/courses' },
];

const BOOK = {
  title: 'Cultural Fluency Without Compromise',
  author: 'Promise Monday',
  desc: 'A field guide for leaders who navigate multiple cultural worlds — without losing themselves in the process.',
  path: '/books/cultural-fluency-without-compromise',
};

// Static article fallbacks per domain (used when API returns nothing)
const STATIC_ARTICLES = [
  { title: 'How Identity Grounds Cultural Intelligence',   summary: 'The link between knowing yourself and leading credibly across cultures.',       domain: 0, slug: null },
  { title: 'The Cost of a Single Perspective',             summary: 'What leaders miss when they fail to shift cultural frames under pressure.',     domain: 1, slug: null },
  { title: 'Communication Without Borders',                summary: 'Adapting your voice and delivery without losing what you actually mean.',       domain: 2, slug: null },
  { title: 'Building Trust Across Cultural Divides',       summary: 'Why inclusive leadership must be structural, not merely personal.',             domain: 3, slug: null },
];

// ── Keyword map for matching live articles to domains ──────────────────────
const DOMAIN_KEYWORDS = {
  0: ['identity', 'values', 'anchor', 'authentic', 'self'],
  1: ['perspective', 'empathy', 'understanding', 'lens', 'assumption'],
  2: ['communication', 'expression', 'voice', 'language', 'context'],
  3: ['relational', 'trust', 'collaboration', 'inclusion', 'team', 'integration'],
};

const matchDomain = (article) => {
  const cats = Array.isArray(article.categories) ? article.categories.join(' ') : (article.category || '');
  const tagsStr = Array.isArray(article.tags) ? article.tags.join(' ') : '';
  const hay = `${article.title} ${cats} ${tagsStr} ${article.excerpt || ''}`.toLowerCase();
  for (const [d, kws] of Object.entries(DOMAIN_KEYWORDS)) {
    if (kws.some(k => hay.includes(k))) return parseInt(d);
  }
  return -1;
};

// ── Small reusable badge ────────────────────────────────────────────────────
const Badge = ({ label, color = 'gold' }) => (
  <span
    className="inline-block text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded font-medium"
    style={{
      background: color === 'gold' ? 'rgba(201,147,22,0.12)' : 'rgba(245,240,232,0.07)',
      color: color === 'gold' ? '#C99316' : 'rgba(245,240,232,0.5)',
      border: color === 'gold' ? '1px solid rgba(201,147,22,0.2)' : '1px solid rgba(245,240,232,0.1)',
    }}
  >
    {label}
  </span>
);

// ── Module card ─────────────────────────────────────────────────────────────
const ModuleCard = ({ mod, isWeakest }) => (
  <Link
    to={mod.path}
    className="group block rounded-xl transition-all duration-300 overflow-hidden"
    style={{
      background: '#12223A',
      border: isWeakest ? '1px solid rgba(201,147,22,0.35)' : '1px solid rgba(245,240,232,0.08)',
    }}
    data-testid={`rec-module-${mod.num}`}
  >
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Badge label="Course Module" />
          {mod.free && <Badge label="Free" color="gold" />}
          {isWeakest && <Badge label="Recommended" color="gold" />}
        </div>
        {!mod.free && <Lock size={12} style={{ color: 'rgba(245,240,232,0.2)' }} />}
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-semibold"
          style={{ background: 'rgba(201,147,22,0.1)', color: '#C99316', border: '1px solid rgba(201,147,22,0.2)' }}
        >
          0{mod.num}
        </div>
        <div>
          <p className="font-serif text-base text-[#F5F0E8] leading-tight">{mod.title}</p>
          <p className="text-[11px] mt-0.5" style={{ color: '#C99316', opacity: 0.7 }}>{mod.subtitle}</p>
        </div>
      </div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(245,240,232,0.55)' }}>{mod.desc}</p>
      <div className="flex items-center justify-between">
        <span className="text-[11px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{mod.duration}</span>
        <span
          className="inline-flex items-center gap-1.5 text-xs group-hover:gap-2.5 transition-all"
          style={{ color: '#C99316' }}
        >
          {mod.free ? 'Start Free' : 'Learn more'} <ArrowRight size={12} />
        </span>
      </div>
    </div>
    {isWeakest && (
      <div style={{ height: '2px', background: 'linear-gradient(to right, rgba(201,147,22,0.6), transparent)' }} />
    )}
  </Link>
);

// ── Book card ───────────────────────────────────────────────────────────────
const BookCard = () => (
  <Link
    to={BOOK.path}
    className="group block rounded-xl transition-all duration-300"
    style={{ background: '#12223A', border: '1px solid rgba(245,240,232,0.08)' }}
    data-testid="rec-book"
  >
    <div className="p-6 flex gap-5">
      <div
        className="w-14 h-20 rounded-lg flex-shrink-0 flex items-center justify-center"
        style={{ background: 'rgba(201,147,22,0.08)', border: '1px solid rgba(201,147,22,0.15)' }}
      >
        <BookOpen size={22} style={{ color: 'rgba(201,147,22,0.7)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <Badge label="Reading" />
        <p className="font-serif text-base text-[#F5F0E8] mt-2 leading-snug">{BOOK.title}</p>
        <p className="text-xs mt-0.5 mb-3" style={{ color: 'rgba(245,240,232,0.35)' }}>by {BOOK.author}</p>
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.55)' }}>{BOOK.desc}</p>
        <span className="inline-flex items-center gap-1.5 text-xs mt-4 group-hover:gap-2.5 transition-all" style={{ color: '#C99316' }}>
          Explore the book <ArrowRight size={12} />
        </span>
      </div>
    </div>
  </Link>
);

// ── Article card ────────────────────────────────────────────────────────────
const ArticleCard = ({ article }) => {
  const path = article.slug ? `/articles/${article.slug}` : '/articles';
  return (
    <Link
      to={path}
      className="group block rounded-xl transition-all duration-300"
      style={{ background: '#12223A', border: '1px solid rgba(245,240,232,0.08)' }}
      data-testid={`rec-article-${article.slug || 'static'}`}
    >
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <Badge label="Article" color="muted" />
          {article.read_time && (
            <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{article.read_time} min read</span>
          )}
        </div>
        <p className="font-serif text-base text-[#F5F0E8] leading-snug mb-2">{article.title}</p>
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.55)' }}>{article.summary || article.excerpt}</p>
        <span className="inline-flex items-center gap-1.5 text-xs mt-4 group-hover:gap-2.5 transition-all" style={{ color: '#C99316' }}>
          Read article <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const CFQRecommendations = ({ domainScores }) => {
  const [articles, setArticles] = useState([]);

  // Fetch live articles from API
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/articles?page=1&limit=30`);
        if (res.ok) {
          const d = await res.json();
          const all = d.articles || d.items || (Array.isArray(d) ? d : []);
          setArticles(all.filter(a => a.status === 'published' || !a.status));
        }
      } catch {}
    })();
  }, []);

  // Rank domains by weakness (lowest % first)
  const ranked = domainScores
    .map((score, idx) => ({ idx, score, pct: score / 25 }))
    .sort((a, b) => a.pct - b.pct);

  const weakest2 = [ranked[0].idx, ranked[1].idx];

  // Pick 2 module cards — weakest domains first
  const moduleRecs = weakest2.map(idx => MODULES[idx]);

  // Pick 2 articles — try live API, fall back to static per weakest domain
  let articleRecs = [];
  if (articles.length > 0) {
    const scored = articles.map(a => ({ ...a, domainMatch: matchDomain(a) }));
    for (const wi of weakest2) {
      const match = scored.find(a => a.domainMatch === wi && !articleRecs.includes(a));
      if (match) { articleRecs.push(match); continue; }
    }
    // If still short, fill with any remaining articles
    if (articleRecs.length < 2) {
      const rest = scored.filter(a => !articleRecs.includes(a));
      articleRecs = [...articleRecs, ...rest].slice(0, 2);
    }
    // Normalise shape
    articleRecs = articleRecs.map(a => ({
      title: a.title,
      summary: a.excerpt || a.summary || '',
      slug: a.slug,
      read_time: a.reading_time || a.read_time,
    }));
  } else {
    // Static fallback — use weakest-domain articles
    articleRecs = weakest2.map(wi => STATIC_ARTICLES[wi]);
  }

  return (
    <div className="py-14 px-6" data-testid="cfq-recommendations">
      <div className="max-w-3xl mx-auto">
        {/* Section header */}
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: '#C99316' }}>Tailored To Your Profile</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F0E8] font-light leading-tight">
            Recommended For You
          </h2>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: 'rgba(245,240,232,0.45)' }}>
            Based on your two lowest-scoring domains — {MODULES[weakest2[0]].title} and {MODULES[weakest2[1]].title} — here are curated resources to accelerate your growth.
          </p>
        </div>

        {/* Modules (2) */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-4">
            <Layers size={13} style={{ color: 'rgba(245,240,232,0.25)' }} />
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'rgba(245,240,232,0.3)' }}>Course Modules</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {moduleRecs.map((mod, i) => (
              <ModuleCard key={mod.num} mod={mod} isWeakest={i === 0} />
            ))}
          </div>
        </div>

        {/* Book (1) */}
        <div className="mb-3 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={13} style={{ color: 'rgba(245,240,232,0.25)' }} />
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'rgba(245,240,232,0.3)' }}>Foundational Reading</span>
          </div>
          <BookCard />
        </div>

        {/* Articles (2) */}
        {articleRecs.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={13} style={{ color: 'rgba(245,240,232,0.25)' }} />
              <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'rgba(245,240,232,0.3)' }}>Editorial Insights</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {articleRecs.map((art, i) => (
                <ArticleCard key={i} article={art} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CFQRecommendations;
